import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DnsRecordsQueryKeys, useAutocompleteDnsRecordsQuery } from '@/modules/DnsRecords';
import {
  DomainConfigFormFields,
  DomainConfigFormInputs,
  DomainConfigurationStatus,
  DomainDeploymentInfo,
  DomainsQueryKeys,
  useAttachConfigDomainsMutation,
  useGetConfigDomainsQuery,
} from '@/modules/Domains';

import { CommonDrawerTitle } from '@/common/layouts';
import { DomainsConfigFormView } from './DomainConfigFormView';
import { domainConfigFormValidationSchema } from './validation';

interface Props {
  domainId: string;
}

export const DomainsConfigCreateFormContainer: FC<Props> = memo(({ domainId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainConfigFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainConfigFormInputs>>(null);

  const proxyServerIdFormValue = Form.useWatch<string>(DomainConfigFormInputs.ProxyServerId, form);
  const dnsRecordIdFormValue = Form.useWatch<string>(DomainConfigFormInputs.DnsRecordId, form);

  const { isSuccess: isDomainDeploymentInfoSuccess } = useGetConfigDomainsQuery(domainId)({
    enabled: false,
  });

  const { mutate: setupConfigMutation, isLoading: setupDeploymentConfigIsLoading } = useAttachConfigDomainsMutation(domainId)({
    onSuccess: () => {
      if (isDomainDeploymentInfoSuccess) {
        queryClient.setQueryData<DomainDeploymentInfo>([DomainsQueryKeys.GetConfig, domainId], (data) => {
          return {
            ...data,
            configurationStatus: DomainConfigurationStatus.Pending,
          } as DomainDeploymentInfo;
        });
      }

      handleBack();
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof DomainConfigFormInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  const initialValues: DomainConfigFormFields = {
    isHttps: false,
    siteId: undefined,
    dnsRecordId: undefined,
    proxyServerAddressId: undefined,
    proxyServerId: undefined,
    wwwRedirect: undefined,
    schemeRedirect: undefined,
    staticPrefix: undefined,
  };

  const handleFinish = useCallback((values: DomainConfigFormFields): void => {
    setFormErrors(null);
    setupConfigMutation({ ...values, staticPrefix: values.staticPrefix || undefined });
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    form.resetFields();

    navigate(AppRoutes.DomainsById.replace(':domainId', domainId));
  }, [domainId]);

  return (
    <>
      <CommonDrawerTitle>Налаштувати домен</CommonDrawerTitle>

      <DomainsConfigFormView
        formName="domain-configuration-setup"
        formType="create"
        form={form}
        formErrors={formErrors}
        initialValues={initialValues}
        isFormLoading={setupDeploymentConfigIsLoading}
        validationSchema={domainConfigFormValidationSchema}
        proxyServerIdFormValue={proxyServerIdFormValue}
        dnsRecordIdFormValue={dnsRecordIdFormValue}
        handleBack={handleBack}
        handleFinish={handleFinish}
        handleFinishFailed={handleFinishFailed}
      />
    </>
  );
});
